import baseapi from './baseapi';
import { withClientIdParams, withClientIdBody, getClientId } from './automationapi';

export interface SourceField {
  key: string;
  label: string;
  type: string;
}

export interface SourceMetadata {
  source: string;
  key: string;
  label: string;
  icon?: string;
  db_table?: string;
  signal_types?: string[];
  allowed_asset_types?: string[];
  record_key_field?: string;
  fields: SourceField[];
  template_variables: string[] | Record<string, string[]>;
  default_template_variables?: string[] | Record<string, string[]>;
  all_template_variables?: string[] | Record<string, string[]>;
}

export interface TextWidget {
  id: string;
  type: 'text';
  content: string;
}

export interface TableColumn {
  label: string;
  field: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
}

export interface TableWidget {
  id: string;
  type: 'table';
  title?: string;
  source?: string; // default "items"
  item_alias?: string; // default "item"
  columns: TableColumn[];
  empty_message?: string;
}

export interface DividerWidget {
  id: string;
  type: 'divider';
}

export type TemplateWidget = TextWidget | TableWidget | DividerWidget;

export interface HtmlTemplateConfig {
  id?: string;
  name?: string;
  // Header & Branding
  logo_url?: string;
  logo_position?: 'top-left' | 'top-center' | 'top-right' | 'inline-left' | 'inline-right';
  logo_width?: string;
  header?: string;
  header_align?: 'left' | 'center' | 'right';
  header_font_size?: string;
  header_font_weight?: string;
  header_text_color?: string;
  header_bg_color?: string;
  // Ordered Widgets List
  widgets: TemplateWidget[];
  // Footer
  footer?: string;
  // Styling Theme
  primary_color?: string;
  background_color?: string;
  card_background?: string;
  text_color?: string;
  muted_color?: string;
  border_color?: string;
  max_width?: string;
  font_family?: string;
}

export interface HtmlTemplateItem {
  id: number | string;
  name: string;
  description?: string;
  source: string;
  organization_id?: number | null;
  client_id?: number | null;
  is_active: boolean;
  is_global?: boolean;
  created?: string;
  updated?: string;
  open_template: HtmlTemplateConfig;
  close_template: HtmlTemplateConfig;
}

export interface HtmlTemplateListParams {
  source?: string;
  organization_id?: number;
  client_id?: number;
  is_active?: boolean;
  search?: string;
  query?: string;
  skip?: number;
  limit?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  include_global?: boolean;
}

export interface CreateHtmlTemplatePayload {
  name: string;
  description?: string;
  source: string;
  organization_id?: number | null;
  client_id?: number | null;
  is_active?: boolean;
  is_global?: boolean;
  open_template: HtmlTemplateConfig;
  close_template: HtmlTemplateConfig;
}

export interface UpdateHtmlTemplatePayload {
  name?: string;
  description?: string;
  source?: string;
  organization_id?: number | null;
  client_id?: number | null;
  is_active?: boolean;
  is_global?: boolean;
  open_template?: HtmlTemplateConfig;
  close_template?: HtmlTemplateConfig;
}

export const sanitizeTemplateConfig = (
  raw: any,
  fallback: HtmlTemplateConfig = { widgets: [] }
): HtmlTemplateConfig => {
  if (!raw) return fallback;

  let parsed = raw;
  while (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      break;
    }
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return fallback;
  }

  const clean: Record<string, any> = {};
  for (const [key, value] of Object.entries(parsed)) {
    // Strip accidental character-index properties ("0", "1", "2", ...) caused by string spreading
    if (!/^\d+$/.test(key)) {
      clean[key] = value;
    }
  }

  let widgets = clean.widgets;
  while (typeof widgets === 'string') {
    try {
      widgets = JSON.parse(widgets);
    } catch {
      break;
    }
  }

  if (Array.isArray(widgets)) {
    widgets = widgets.map((w: any) => {
      if (typeof w === 'string') {
        try {
          return JSON.parse(w);
        } catch {
          return w;
        }
      }
      return w;
    });
  }

  return {
    ...fallback,
    ...clean,
    widgets: Array.isArray(widgets) ? widgets : fallback.widgets || [],
  };
};

export const sanitizeTemplateItem = (item: any): HtmlTemplateItem => {
  if (!item) return item;
  const actual = Array.isArray(item) ? (item[0] || {}) : item;
  if (!actual || typeof actual !== 'object') return actual;
  return {
    ...actual,
    open_template: sanitizeTemplateConfig(actual.open_template),
    close_template: sanitizeTemplateConfig(actual.close_template),
  };
};

export const htmlTemplatesApi = {
  // 1. Get all supported sources
  getSources: async () => {
    const res = await baseapi.get<{
      status: boolean;
      total: number;
      data: SourceMetadata[];
    }>('/html_templates/sources', {
      params: withClientIdParams(),
    });
    return res.data;
  },

  // 2. Get fields and template variables for a single source
  getSourceDetails: async (source: string) => {
    const res = await baseapi.get<any>(
      `/html_templates/sources/${encodeURIComponent(source)}`,
      {
        params: withClientIdParams(),
      }
    );
    return (res.data?.data || res.data) as SourceMetadata;
  },

  // 3. List templates with filtering and pagination
  getTemplates: async (params?: HtmlTemplateListParams) => {
    const res = await baseapi.get<{
      status: boolean;
      total: number;
      data: HtmlTemplateItem[];
    }>('/html_templates/', {
      params: withClientIdParams(params),
    });
    const items = (res.data?.data || []).map(sanitizeTemplateItem);
    return {
      ...res.data,
      data: items,
    };
  },

  // 4. Get template by ID with safe unwrapping
  getTemplateById: async (id: string | number) => {
    const res = await baseapi.get<any>(`/html_templates/${id}`, {
      params: withClientIdParams(),
    });
    const body = res.data;
    let rawItem = body;
    if (Array.isArray(body)) {
      rawItem = body[0];
    } else if (body?.data !== undefined && body?.data !== null) {
      rawItem = Array.isArray(body.data) ? body.data[0] : body.data;
    } else if (body?.template !== undefined && body?.template !== null) {
      rawItem = Array.isArray(body.template) ? body.template[0] : body.template;
    }
    const sanitized = sanitizeTemplateItem(rawItem);
    return {
      ...res.data,
      data: sanitized,
    };
  },

  // 5. Create new template
  createTemplate: async (data: CreateHtmlTemplatePayload) => {
    const clientId = getClientId();
    const isGlobal = clientId === undefined;

    const payloadData: any = {
      ...data,
      open_template: sanitizeTemplateConfig(data.open_template),
      close_template: sanitizeTemplateConfig(data.close_template),
    };

    if (isGlobal) {
      payloadData.is_global = true;
      delete payloadData.client_id;
    } else {
      payloadData.is_global = false;
      payloadData.client_id = data.client_id !== undefined && data.client_id !== null ? data.client_id : clientId;
    }

    const res = await baseapi.post<any>('/html_templates/', payloadData, {
      params: withClientIdParams(),
    });
    const rawData = res.data?.data || (res.data?.id ? res.data : (res.data?.template || res.data));
    return {
      ...res.data,
      data: sanitizeTemplateItem(rawData),
    };
  },

  // 6. Update template (partial or full)
  updateTemplate: async (id: string | number, data: UpdateHtmlTemplatePayload) => {
    const sanitizedData = {
      ...data,
      ...(data.open_template ? { open_template: sanitizeTemplateConfig(data.open_template) } : {}),
      ...(data.close_template ? { close_template: sanitizeTemplateConfig(data.close_template) } : {}),
    };
    const res = await baseapi.put<any>(`/html_templates/${id}`, withClientIdBody(sanitizedData), {
      params: withClientIdParams(),
    });
    const rawData = res.data?.data || (res.data?.id ? res.data : (res.data?.template || res.data));
    return {
      ...res.data,
      data: sanitizeTemplateItem(rawData),
    };
  },

  // 7. Delete template
  deleteTemplate: async (id: string | number) => {
    const res = await baseapi.delete<{
      status: boolean;
      data?: { id: number | string; is_deleted?: boolean };
      message?: string;
    }>(`/html_templates/${id}`, {
      params: withClientIdParams(),
    });
    return res.data;
  },

  // 8. Upload public image or logo to S3
  uploadImage: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await baseapi.post<UploadImageResponse>('/html_templates/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        params: withClientIdParams(),
      });
      const url = res.data?.url || res.data?.data?.url || '';
      return {
        ...res.data,
        url,
      };
    } catch (err: any) {
      if (err.response?.status === 404) {
        const res = await baseapi.post<UploadImageResponse>('/html_templates/upload-image', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          params: withClientIdParams(),
        });
        const url = res.data?.url || res.data?.data?.url || '';
        return {
          ...res.data,
          url,
        };
      }
      throw err;
    }
  },
};

export interface UploadImageResponse {
  status: boolean;
  message?: string;
  url?: string;
  data?: {
    url?: string;
  };
}
